import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { ServerVersion } from "../api/types";
import { FAMILY_TZ } from "../lib/money";

/**
 * Two lines in the corner naming what is running: the bundle you were served,
 * and the server answering it.
 *
 * It exists because "is the thing I just deployed the thing I am looking at?"
 * was not answerable without fetching the JS bundle and grepping it. /healthz
 * cannot answer it either — it returns ok from every build alike, so a deploy
 * that failed to swap the binary looks exactly like one that worked.
 *
 * The server's status is the dot on its own line rather than a third line: a
 * server that can answer is up, and one that cannot has no version to print, so
 * the two facts are really one.
 */
export function BuildInfo() {
  const [server, setServer] = useState<ServerVersion | null>(null);
  const [reachable, setReachable] = useState<boolean | null>(null);

  useEffect(() => {
    // Fetched once on mount, deliberately not polled. This is a footer; a
    // server that is down should read as down, not generate a request every
    // few seconds for the rest of the session.
    let cancelled = false;

    api
      .version()
      .then((v) => {
        if (cancelled) return;
        setServer(v);
        setReachable(true);
      })
      .catch(() => {
        if (!cancelled) setReachable(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="build-info" aria-label="Build information">
      <div className="build-info__line">
        client {__BUILD_COMMIT__} ({formatBuildTime(__BUILD_TIME__)})
      </div>
      <div className="build-info__line">
        <span
          className={`build-info__dot build-info__dot--${statusClass(reachable)}`}
          aria-hidden="true"
        />
        {server
          ? `server ${server.commit} (${formatBuildTime(server.builtAt)})`
          : reachable === false
            ? "server unreachable"
            : "server …"}
      </div>
    </div>
  );
}

function statusClass(reachable: boolean | null): string {
  if (reachable === null) return "pending";
  return reachable ? "up" : "down";
}

/**
 * Renders a build timestamp as `YYYY-MM-DD HH:MM` in the family's timezone.
 *
 * Both timestamps are stamped in UTC, but per the project's rule every date a
 * user reads is Eastern — so this must not fall back to the device's zone, even
 * though a build time is a developer-facing detail.
 *
 * The two times routinely disagree by however long the two pipelines took, and
 * that is the point: seeing them drift apart is how a half-finished deploy
 * announces itself.
 */
function formatBuildTime(iso: string): string {
  if (!iso) return "unknown";

  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "unknown";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: FAMILY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(at);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  // en-CA gives ISO-ordered date parts; assembling by name rather than reading
  // the formatted string keeps the separator out of the locale's hands.
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}
